import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Building2, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { get, post, put } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { COUNTRIES, CURRENCIES, GOAL_KEYS, countryName } from "@/lib/options";
import { BrandLogo } from "@/components/common/BrandLogo";
import { Field, TextField, TextareaField, LinesField, CsvField } from "@/components/common/FormField";
import { UserMenu } from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

const STEPS = ["company", "essentials", "done"];

const Stepper = ({ current }) => {
  const { t } = useTranslation();
  return (
    <ol className="flex items-center gap-2" data-testid="onboarding-stepper">
      {STEPS.map((step, i) => {
        const state = i < current ? "done" : i === current ? "active" : "todo";
        return (
          <li key={step} className="flex items-center gap-2">
            <span className={cn("flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold", state === "done" && "border-primary bg-primary text-primary-foreground", state === "active" && "border-primary text-primary", state === "todo" && "border-border text-muted-foreground")}>
              {state === "done" ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={cn("hidden text-sm sm:inline", state === "active" ? "font-medium text-foreground" : "text-muted-foreground")}>{t(`onboarding.steps.${step}`)}</span>
            {i < STEPS.length - 1 && <span className="mx-1 h-px w-6 bg-border sm:w-10" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
};

export default function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const { user, currentOrg, organizations, refreshOrganizations, switchOrganization, patchCurrentOrg } = useAuth();
  const forceNew = params.get("new") === "1";

  // If the user already has an organization whose onboarding is pending, resume at the essentials step.
  const pendingOrg = useMemo(() => (forceNew ? null : (currentOrg && !currentOrg.onboarding_completed ? currentOrg : organizations.find((o) => !o.onboarding_completed) || null)), [forceNew, currentOrg, organizations]);
  const [step, setStep] = useState(pendingOrg ? 1 : 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (pendingOrg && pendingOrg.id !== currentOrg?.id) switchOrganization(pendingOrg.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOrg?.id]);

  const [org, setOrg] = useState({ name: "", industry: "", country: "CH", default_currency: "CHF", website: "" });
  const [brain, setBrain] = useState({ brand_name: "", short_description: "", description: "", services: [], target_audience: "", tone: "", geo_zones: [], city: "", primary_goal: "", goals: [] });
  const [result, setResult] = useState(null);
  const setB = (k) => (v) => setBrain((b) => ({ ...b, [k]: v }));

  const activeOrgId = pendingOrg?.id || (step > 0 ? currentOrg?.id : null);
  const { data: existingBrain } = useQuery({ queryKey: ["brand-brain", activeOrgId], queryFn: () => get("/brand-brain"), enabled: !!activeOrgId && step === 1 });
  useEffect(() => {
    if (existingBrain) {
      // Server values only prefill what the user has not typed yet: the GET can resolve a few seconds
      // after the step is displayed (remote DB), so it must never wipe an in-progress entry.
      const serverServices = (existingBrain.products_services || []).map((s) => s.name).filter(Boolean);
      setBrain((b) => ({
        ...b,
        brand_name: b.brand_name || existingBrain.brand_name || "",
        short_description: b.short_description || existingBrain.short_description || "",
        description: b.description || existingBrain.description || "",
        services: b.services.length ? b.services : serverServices,
        target_audience: b.target_audience || existingBrain.target_audience || "",
        tone: b.tone || existingBrain.tone || "",
        geo_zones: b.geo_zones.length ? b.geo_zones : existingBrain.geo_zones || [],
        city: b.city || existingBrain.location?.city || "",
        primary_goal: b.primary_goal || existingBrain.primary_goal || "",
        goals: b.goals.length ? b.goals : existingBrain.goals || [],
      }));
    }
  }, [existingBrain]);

  const createOrg = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await post("/organizations", { name: org.name.trim(), industry: org.industry.trim() || null, country: org.country || null, default_currency: org.default_currency, default_locale: i18n.language || "fr-CH", website: org.website.trim() || null });
      await refreshOrganizations(created.id);
      switchOrganization(created.id);
      toast.success(t("onboarding.orgCreated"));
      setStep(1);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    await post("/organizations/current/complete-onboarding");
    patchCurrentOrg({ onboarding_completed: true });
    await refreshOrganizations(activeOrgId || currentOrg?.id);
    queryClient.invalidateQueries({ queryKey: ["brand-brain"], refetchType: "all" });
    queryClient.invalidateQueries({ queryKey: ["members"], refetchType: "all" });
  };

  const saveEssentials = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = {
        brand_name: brain.brand_name.trim() || undefined,
        short_description: brain.short_description.trim() || undefined,
        description: brain.description.trim() || undefined,
        products_services: brain.services.length ? brain.services.map((name) => ({ name, description: "", price: "" })) : undefined,
        target_audience: brain.target_audience.trim() || undefined,
        tone: brain.tone.trim() || undefined,
        geo_zones: brain.geo_zones.length ? brain.geo_zones : undefined,
        primary_goal: brain.primary_goal ? t(`brandBrain.goalOptions.${brain.primary_goal}`) : undefined,
        goals: brain.goals.length ? brain.goals : undefined,
        location: brain.city.trim() ? { ...(existingBrain?.location || {}), city: brain.city.trim() } : undefined,
      };
      const saved = await put("/brand-brain?source=onboarding", payload);
      setResult(saved);
      queryClient.setQueryData(["brand-brain", activeOrgId || currentOrg?.id], saved);
      toast.success(t("onboarding.brainSaved"));
      await complete();
      setStep(2);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setBusy(true);
    try {
      await complete();
      navigate("/app", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const orgName = currentOrg?.name || pendingOrg?.name || org.name;

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-page">
      <header className="flex h-14 items-center justify-between border-b border-border bg-card px-4 sm:px-6">
        <BrandLogo />
        <UserMenu />
      </header>
      <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:py-12">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="og-section-label">{t("onboarding.stepOf", { current: step + 1, total: STEPS.length })}</div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight md:text-[28px]" data-testid="onboarding-title">
              {step === 0 && (forceNew ? t("onboarding.newOrgTitle") : t("onboarding.companyTitle"))}
              {step === 1 && t("onboarding.essentialsTitle")}
              {step === 2 && t("onboarding.doneTitle")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {step === 0 && t("onboarding.companySubtitle")}
              {step === 1 && t("onboarding.essentialsSubtitle")}
              {step === 2 && t("onboarding.doneSubtitle", { org: orgName, percent: result?.completeness_score ?? 0 })}
            </p>
          </div>
          <Stepper current={step} />
        </div>

        {error && <Alert className="mb-4 border-rose-200 bg-rose-50 text-rose-900" data-testid="onboarding-error"><AlertDescription>{error}</AlertDescription></Alert>}

        {step === 0 && (
          <form onSubmit={createOrg} className="og-card space-y-5 p-6 sm:p-7" data-testid="onboarding-company-form">
            <div className="flex items-center gap-3 text-sm text-muted-foreground"><Building2 className="h-4 w-4 text-primary" />{t("onboarding.welcome", { name: user?.full_name?.split(" ")[0] || "" })}</div>
            <TextField id="onboarding-org-name" label={t("onboarding.companyName")} value={org.name} onChange={(v) => setOrg((o) => ({ ...o, name: v }))} required minLength={2} testId="onboarding-org-name-input" />
            <TextField id="onboarding-industry" label={t("onboarding.industry")} placeholder={t("onboarding.industryPlaceholder")} value={org.industry} onChange={(v) => setOrg((o) => ({ ...o, industry: v }))} required testId="onboarding-industry-input" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="onboarding-country" label={t("onboarding.country")}>
                <Select value={org.country} onValueChange={(v) => setOrg((o) => ({ ...o, country: v }))}>
                  <SelectTrigger id="onboarding-country" data-testid="onboarding-country-select" className="bg-card"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{countryName(c, i18n.language)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field id="onboarding-currency" label={t("onboarding.currency")}>
                <Select value={org.default_currency} onValueChange={(v) => setOrg((o) => ({ ...o, default_currency: v }))}>
                  <SelectTrigger id="onboarding-currency" data-testid="onboarding-currency-select" className="bg-card"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <TextField id="onboarding-website" label={t("onboarding.website")} optional type="url" placeholder="https://" value={org.website} onChange={(v) => setOrg((o) => ({ ...o, website: v }))} testId="onboarding-website-input" />
            <div className="flex justify-end pt-2">
              <Button type="submit" className="h-10" disabled={busy || org.name.trim().length < 2 || !org.industry.trim()} data-testid="onboarding-create-org-button">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("onboarding.createOrg")}<ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </form>
        )}

        {step === 1 && (
          <form onSubmit={saveEssentials} className="og-card space-y-5 p-6 sm:p-7" data-testid="onboarding-essentials-form">
            <div className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"><Sparkles className="h-4 w-4 shrink-0" />{t("onboarding.existingOrg", { org: orgName })}</div>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField id="onboarding-brand-name" label={t("onboarding.brandName")} optional value={brain.brand_name} onChange={setB("brand_name")} testId="onboarding-brand-name-input" />
              <TextField id="onboarding-city" label={t("onboarding.city")} optional value={brain.city} onChange={setB("city")} testId="onboarding-city-input" />
            </div>
            <TextareaField id="onboarding-description" label={t("onboarding.description")} essential placeholder={t("onboarding.descriptionPlaceholder")} value={brain.description} onChange={setB("description")} rows={4} testId="onboarding-description-input" />
            <LinesField id="onboarding-services" label={t("onboarding.services")} essential hint={t("onboarding.servicesHint")} value={brain.services} onChange={setB("services")} rows={3} testId="onboarding-services-input" />
            <TextareaField id="onboarding-target" label={t("onboarding.target")} essential value={brain.target_audience} onChange={setB("target_audience")} rows={3} testId="onboarding-target-input" />
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField id="onboarding-tone" label={t("onboarding.tone")} essential placeholder={t("onboarding.tonePlaceholder")} value={brain.tone} onChange={setB("tone")} testId="onboarding-tone-input" />
              <CsvField id="onboarding-geo" label={t("onboarding.geo")} essential hint={t("onboarding.geoHint")} value={brain.geo_zones} onChange={setB("geo_zones")} testId="onboarding-geo-input" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="onboarding-primary-goal" label={t("onboarding.primaryGoal")} essential>
                <Select value={brain.primary_goal || ""} onValueChange={setB("primary_goal")}>
                  <SelectTrigger id="onboarding-primary-goal" data-testid="onboarding-primary-goal-select" className="bg-card"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent className="bg-card">{GOAL_KEYS.map((g) => <SelectItem key={g} value={g}>{t(`brandBrain.goalOptions.${g}`)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <LinesField id="onboarding-goals" label={t("onboarding.goals")} hint={t("onboarding.goalsHint")} value={brain.goals} onChange={setB("goals")} rows={3} testId="onboarding-goals-input" />
            </div>
            <div className="flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <Button type="button" variant="ghost" onClick={skip} disabled={busy} data-testid="onboarding-skip-button">{t("onboarding.later")}</Button>
                <p className="px-4 text-xs text-muted-foreground sm:px-0 sm:pl-4">{t("onboarding.laterHint")}</p>
              </div>
              <Button type="submit" className="h-10" disabled={busy || !brain.description.trim() || !brain.target_audience.trim()} data-testid="onboarding-save-essentials-button">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("onboarding.saveContinue")}<ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="onboarding-done">
            <div className="og-card p-6 sm:p-7">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{t("brandBrain.completeness")}</span>
                <span className="font-heading text-lg font-semibold" data-testid="onboarding-completeness">{t("common.percent", { value: result?.completeness_score ?? 0 })}</span>
              </div>
              <Progress value={result?.completeness_score ?? 0} className="mt-2 h-2" />
              <div className={cn("mt-4 rounded-lg border px-3 py-2 text-sm", result?.readiness?.ready_for_agents ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-950")} data-testid="onboarding-readiness">
                {result?.readiness?.ready_for_agents ? t("brandBrain.readyHint") : t("brandBrain.notReadyHint")}
              </div>
              <div className="mt-6">
                <div className="og-section-label mb-2">{t("onboarding.next.title")}</div>
                <ul className="space-y-2 text-sm">
                  {["one", "two", "three"].map((k) => (
                    <li key={k} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-primary" />{t(`onboarding.next.${k}`)}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button variant="outline" className="bg-card" onClick={() => navigate("/app/brand-brain", { replace: true })} data-testid="onboarding-open-brand-brain">{t("onboarding.openBrandBrain")}</Button>
                <Button onClick={() => navigate("/app", { replace: true })} data-testid="onboarding-open-dashboard">{t("onboarding.openDashboard")}<ArrowRight className="ml-2 h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
